import type { Problem, AlgorithmProblem, ClassDesignProblem, ReactProblem, FullstackProblem, AgentProblem } from './_types';

import abs from './abs.json';
import agentbank from './agent.json';
import arr from './arr.json';
import async_ from './async.json';
import attn from './attn.json';
import auth from './auth.json';
import bs from './bs.json';
import bt from './bt.json';
import cache from './cache.json';
import closure from './closure.json';
import cls from './cls.json';
import ctx from './ctx.json';
import design from './design.json';
import dp from './dp.json';
import eff from './eff.json';
import embed from './embed.json';
import enc from './enc.json';
import evals from './evals.json';
import fa from './fa.json';
import fsbank from './fs.json';
import ft from './ft.json';
import gen from './gen.json';
import gql from './gql.json';
import gr from './gr.json';
import graph from './graph.json';
import grpc from './grpc.json';
import hash from './hash.json';
import hk from './hk.json';
import id_ from './id.json';
import iface from './iface.json';
import inf from './inf.json';
import inh from './inh.json';
import jssyn from './jssyn.json';
import jsx from './jsx.json';
import kv from './kv.json';
import lb from './lb.json';
import legacy from './legacy.json';
import ll from './ll.json';
import mcp from './mcp.json';
import pat from './pat.json';
import perf from './perf.json';
import poly from './poly.json';
import props from './props.json';
import pysyn from './pysyn.json';
import q from './q.json';
import q1 from './q1.json';
import rag from './rag.json';
import rest from './rest.json';
import rl from './rl.json';
import shrd from './shrd.json';
import slid from './slid.json';
import solid from './solid.json';
import srt from './srt.json';
import state from './state.json';
import stk from './stk.json';
import str from './str.json';
import tok from './tok.json';
import toolskill from './tool.json';
import tree from './tree.json';
import two from './two.json';
import ver from './ver.json';
import wbh from './wbh.json';

const banks: unknown[][] = [
  abs, agentbank, arr, async_, attn, auth, bs, bt, cache, closure, cls,
  ctx, design, dp, eff, embed, enc, evals, fa, fsbank, ft, gen,
  gql, gr, graph, grpc, hash, hk, id_, iface, inf, inh,
  jssyn, jsx, kv, lb, legacy, ll, mcp, pat, perf, poly, props,
  pysyn, q, q1, rag, rest, rl, shrd, slid, solid, srt,
  state, stk, str, tok, toolskill, tree, two, ver, wbh,
];

// IDE-runnable problems: the existing algo / class runtimes consume these via
// Pyodide and the JS class harness, and `fullstack` problems run through the same
// CodeCompiler + multi-file editor (backend dispatched in the Pyodide / JS worker).
// React problems live in jsx.json but run in the sibling iframe harness
// (frontend/public/react.harness.html), NOT via this loader. They're materialized
// separately as `jsonReactProblems` so the IDE's main code paths don't have to
// type-guard ReactProblem at every call site.
export type IdeRunnableProblem = AlgorithmProblem | ClassDesignProblem | FullstackProblem | AgentProblem;

function isIdeRunnable(p: Problem): p is IdeRunnableProblem {
  return p.type === 'algorithm' || p.type === 'class' || p.type === 'fullstack' || p.type === 'agent';
}

// The bundled JSON bank is now only a *seed*: the source of truth is the
// DynamoDB store, fetched once on boot via `GET /api/problems/all` (see
// ProblemStoreProvider). Seeding from the bundle gives an instant first paint and
// a dev/offline fallback; `replaceAllProblems` then swaps in the live set and
// `upsert`/`remove` apply live admin edits — all without moving any call site.
const seedFromDisk = banks.flat() as Problem[];

// ── Mutable store ───────────────────────────────────────────────────────────
// The exported arrays keep a *stable identity* (callers `import` them directly);
// on every store change we replace their contents in place via splice so existing
// references see the new bank. Components re-read them after ProblemStoreProvider
// bumps its version counter (forcing a re-render). The accessor functions read the
// live `_all` array, so they're always current.

let _all: Problem[] = [];

export const jsonProblems: IdeRunnableProblem[] = [];
export const jsonReactProblems: ReactProblem[] = [];
export const jsonAlgorithmProblems: AlgorithmProblem[] = [];
export const jsonClassDesignProblems: ClassDesignProblem[] = [];
export const jsonFullstackProblems: FullstackProblem[] = [];
export const jsonAgentProblems: AgentProblem[] = [];

const idIndex = new Map<string, IdeRunnableProblem>();
const reactIdIndex = new Map<string, ReactProblem>();

function replaceContents<T>(arr: T[], next: readonly T[]): void {
  arr.splice(0, arr.length, ...next);
}

function rebuild(all: Problem[]): void {
  _all = all;
  const ide = all.filter(isIdeRunnable);
  const react = all.filter((p): p is ReactProblem => p.type === 'react');

  replaceContents(jsonProblems, ide);
  replaceContents(jsonReactProblems, react);
  replaceContents(jsonAlgorithmProblems, ide.filter((p): p is AlgorithmProblem => p.type === 'algorithm'));
  replaceContents(jsonClassDesignProblems, ide.filter((p): p is ClassDesignProblem => p.type === 'class'));
  replaceContents(jsonFullstackProblems, ide.filter((p): p is FullstackProblem => p.type === 'fullstack'));
  replaceContents(jsonAgentProblems, ide.filter((p): p is AgentProblem => p.type === 'agent'));

  idIndex.clear();
  ide.forEach(p => idIndex.set(p.id, p));
  reactIdIndex.clear();
  react.forEach(p => reactIdIndex.set(p.id, p));
}

rebuild(seedFromDisk); // initial seed; ProblemStoreProvider swaps in the live set

// ── Mutators (used by ProblemStoreProvider after API hydrate / admin edits) ──

/** Replace the entire bank — called once on boot with `GET /api/problems/all`. */
export function replaceAllProblems(problems: Problem[]): void {
  rebuild(problems);
}

/** Insert or replace one problem (admin create/edit), keeping the rest. */
export function upsertProblem(problem: Problem): void {
  rebuild([..._all.filter(p => p.id !== problem.id), problem]);
}

/** Drop one problem (admin delete). */
export function removeProblemById(id: string): void {
  rebuild(_all.filter(p => p.id !== id));
}

/** Live full bank (all types). */
export function getAllProblems(): Problem[] {
  return _all;
}

export function findProblemById(id: string): IdeRunnableProblem | undefined {
  return idIndex.get(id);
}

/** Separate lookup for React problems — they're routed to a sibling IDE pane,
 *  not the main CodeCompiler narrowed-Problem code path. */
export function findReactProblemById(id: string): ReactProblem | undefined {
  return reactIdIndex.get(id);
}

// Skill/realm lookups include React problems too — they're in the bank with
// realmId/skillId set, even though they don't run through the IDE harness.
// Without this, the React realm's badge and any react skill's "done" state
// would always show 0.
export function problemsBySkillId(skillId: string): Problem[] {
  return _all.filter(p => p.skillId === skillId);
}

export function problemsByRealmId(realmId: string): Problem[] {
  return _all.filter(p => p.realmId === realmId);
}

// Lightweight per-problem record used by the global search modal. Mirrors
// what `/api/problems/index` returns, so the search path is identical whether
// the bank came from the bundle seed or the API.
export interface CatalogueEntry {
  id: string;
  title: string;
  skillId: string;
  realmId: string;
  type: 'algorithm' | 'class' | 'react' | 'fullstack' | 'agent';
  difficulty: 'Easy' | 'Medium' | 'Hard';
  summary?: string;
  tags?: string[];
}

// Rebuilt from live state on each call so admin adds/edits show in search
// (the modal remounts on open, so this re-runs then).
export function getCatalogue(): CatalogueEntry[] {
  return _all.map(p => ({
    id: p.id,
    title: p.title,
    skillId: p.skillId,
    realmId: p.realmId,
    type: p.type,
    difficulty: p.difficulty,
    summary: p.summary,
    tags: p.tags,
  }));
}
